import { FC, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import { io, Socket } from 'socket.io-client';
import { IUploadRequest, IUploadStatus, UPLOAD_PROVIDERS, UploadField, UploadProvider } from 'types/upload-status';
import { getProviderUploads, hasUploadIssue, isUploadBusy, isUploadTerminal } from 'utils/helpers/upload-status';
import { DataSettingsService } from 'utils/services/data-settings';
import SocketContext, { IAuditProcessesStatus } from './context';

const URL = process.env.REACT_APP_ADMIN_SOCKET_API_URL;

export const DataSettingsSocketProvider: FC<PropsWithChildren> = (props) => {
	const [isConnected, setIsConnected] = useState(false);
	const [processesStatus, setProgressesStatusData] = useState<IAuditProcessesStatus>({});

	const [uploads, setUploads] = useState<IUploadStatus[]>([]);
	const [requests, setRequests] = useState<Partial<Record<UploadProvider, IUploadRequest>>>({});
	const socketRef = useRef<Socket>();
	const pendingProviders = useRef(new Set<UploadProvider>());
	const providerUploads = useMemo(() => getProviderUploads(uploads, requests), [uploads, requests]);
	const hasActiveUploads = Object.values(providerUploads).some(isUploadBusy);
	const hasIncompleteUploads = Object.values(providerUploads).some(hasUploadIssue);

	useEffect(() => {
		setRequests((previous) => {
			const next = { ...previous };
			let changed = false;
			Object.values(previous).forEach((request) => {
				if (
					request.state !== 'pending' &&
					uploads.some(
						(upload) =>
							upload.uploadId === request.uploadId && (request.state === 'accepted' || isUploadTerminal(upload))
					)
				) {
					delete next[request.provider];
					changed = true;
				}
			});
			return changed ? next : previous;
		});
	}, [uploads, requests]);

	useEffect(() => {
		if (!URL) return undefined;
		const socket = io(URL);
		socketRef.current = socket;
		const handleConnect = () => setIsConnected(true);
		const handleDisconnect = () => setIsConnected(false);
		socket.on('connect', handleConnect);
		socket.on('disconnect', handleDisconnect);
		socket.on('connect_error', handleDisconnect);
		socket.on('processesStatus', setProgressesStatusData);
		socket.on('uploadsStatus', setUploads);
		return () => {
			socket.off('connect', handleConnect);
			socket.off('disconnect', handleDisconnect);
			socket.off('connect_error', handleDisconnect);
			socket.off('processesStatus', setProgressesStatusData);
			socket.off('uploadsStatus', setUploads);
			socket.disconnect();
			socketRef.current = undefined;
		};
	}, []);

	const startUpload = async (field: UploadField, file: File): Promise<boolean> => {
		const provider = UPLOAD_PROVIDERS[field];
		const socket = socketRef.current;
		if (
			!socket?.connected ||
			pendingProviders.current.has(provider) ||
			(providerUploads[provider] && isUploadBusy(providerUploads[provider]!))
		)
			return false;
		const uploadId = nanoid();
		pendingProviders.current.add(provider);
		setRequests((previous) => ({
			...previous,
			[provider]: {
				uploadId,
				provider,
				fileName: file.name,
				startedAt: new Date().toISOString(),
				state: 'pending',
				previousUploadIds: uploads.filter((upload) => upload.provider === provider).map((upload) => upload.uploadId),
			},
		}));
		const formData = new FormData();
		formData.append('file', file);
		const updateRequest = (state: IUploadRequest['state']) =>
			setRequests((previous) => {
				const request = previous[provider];
				return request?.uploadId === uploadId ? { ...previous, [provider]: { ...request, state } } : previous;
			});
		try {
			await DataSettingsService.etlFilesParsing(field, formData, { uploadId, socketId: socket.id });
			updateRequest('accepted');
			return true;
		} catch {
			updateRequest('error');
			return false;
		} finally {
			pendingProviders.current.delete(provider);
		}
	};

	return (
		<SocketContext.Provider
			value={{
				processesStatus,
				isConnected,
				uploads,
				providerUploads,
				hasActiveUploads,
				hasIncompleteUploads,
				startUpload,
			}}
		>
			{props.children}
		</SocketContext.Provider>
	);
};
